import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatErr, pdfUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, ArrowLeft, Upload, Trash2, Plus, Download, X, FlaskConical, Pill, Stethoscope, ClipboardList, Camera, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const FINDING_PARAMS = ["Mites", "Yeast", "Bacteria", "Fungal Elements"];
const FINDING_RESULTS = ["Positive", "Negative", "Not Tested"];

const initial = {
  patient_id: "",
  chief_complaint: "", history: "",
  temperature: "", heart_rate: "", respiratory_rate: "", hydration: "",
  lymph_node: "", mucus_membrane: "",
  observations: "", observation_images: [],
  diagnostic_tests: [], tests_notes: "",
  findings_text: "", findings: {}, additional_findings: "",
  primary_diagnosis: "", secondary_diagnosis: "", differential_diagnosis: "", icd_code: "", diagnosis_text: "",
  treatments: [], treatment_text: "",
  medicines: [], recommendations: "",
  follow_up_date: "", follow_up_notes: "",
  follow_up_reminders: [],
  doctor_name: "", registration_number: "", signature_file_id: null,
};

const SECTIONS = [
  { id: "patient", label: "Patient", icon: ClipboardList },
  { id: "consultation", label: "Consultation", icon: Stethoscope },
  { id: "exam", label: "Clinical Exam", icon: Stethoscope },
  { id: "observations", label: "Observations", icon: Camera },
  { id: "tests", label: "Diagnostic Tests", icon: FlaskConical },
  { id: "findings", label: "Findings", icon: FlaskConical },
  { id: "diagnosis", label: "Diagnosis", icon: ClipboardList },
  { id: "treatment", label: "Treatment", icon: Pill },
  { id: "medicines", label: "Medicines", icon: Pill },
  { id: "followup", label: "Follow-up", icon: ClipboardList },
  { id: "vet", label: "Veterinarian", icon: ClipboardList },
];

function PatientCombobox({ value, patients, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = patients.find((p) => p.id === value);
  const filtered = !q ? patients.slice(0, 50) : patients.filter((p) => {
    const s = q.toLowerCase();
    return `${p.pet_name} ${p.owner_name} ${p.phone} ${p.case_number} ${p.breed || ""}`.toLowerCase().includes(s);
  }).slice(0, 50);
  return (
    <div className="relative">
      <Input data-testid="patient-search-combobox" placeholder="Type pet name, owner, phone, case #…"
             value={open ? q : (selected ? `${selected.pet_name} — ${selected.owner_name} (${selected.case_number})` : q)}
             onFocus={() => setOpen(true)}
             onChange={(e) => { setQ(e.target.value); setOpen(true); if (selected) onChange(""); }}
             onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {filtered.length === 0 && <div className="p-3 text-sm text-muted-foreground">No matching patients.</div>}
          {filtered.map((p) => (
            <button key={p.id} type="button"
                    data-testid={`patient-option-${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onChange(p.id); setQ(""); setOpen(false); }}
                    className="block w-full text-left px-3 py-2 hover:bg-muted text-sm">
              <div className="font-medium">{p.pet_name} <span className="text-muted-foreground font-normal">— {p.species}{p.breed ? `, ${p.breed}` : ""}</span></div>
              <div className="text-xs text-muted-foreground">{p.owner_name} · {p.phone} · {p.case_number}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ConsultationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initialPatient = sp.get("patient");

  const [data, setData] = useState({ ...initial, patient_id: initialPatient || "" });
  const [patients, setPatients] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [medicineCatalog, setMedicineCatalog] = useState([]);
  const [tests, setTests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(id || null);
  const [autosaveTag, setAutosaveTag] = useState("");
  const [active, setActive] = useState("patient");

  // Load reference data
  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/treatments").then((r) => setTreatments(r.data));
    api.get("/medicines").then((r) => setMedicineCatalog(r.data));
    api.get("/diagnostic-tests").then((r) => setTests(r.data));
    api.get("/auth/me").then((r) => {
      setData((d) => ({ ...d, doctor_name: d.doctor_name || r.data.name, registration_number: d.registration_number || r.data.registration_number || "" }));
    });
  }, []);

  useEffect(() => {
    if (id) api.get(`/consultations/${id}`).then((r) => setData(r.data));
  }, [id]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Auto-save (debounced) after first save
  const debounceRef = useRef();
  useEffect(() => {
    if (!savedId || !data.patient_id) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await api.put(`/consultations/${savedId}`, data);
        setAutosaveTag(new Date().toLocaleTimeString());
      } catch (err) {
        console.warn("autosave failed:", err);
      }
    }, 1500);
    return () => clearTimeout(debounceRef.current);
  }, [data, savedId]);

  const onSave = async () => {
    if (!data.patient_id) { toast.error("Please select a patient"); return; }
    setBusy(true);
    try {
      const res = savedId
        ? await api.put(`/consultations/${savedId}`, data)
        : await api.post("/consultations", data);
      setSavedId(res.data.id);
      toast.success("Consultation saved");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Upload images for observations
  const fileInputRef = useRef();
  const onFiles = async (files) => {
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data: rec } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        setData((d) => ({ ...d, observation_images: [...(d.observation_images || []), { file_id: rec.id, caption: "" }] }));
      } catch (e) { toast.error("Upload failed"); }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) onFiles(Array.from(e.dataTransfer.files));
  };

  const toggleList = (key, val) => {
    const arr = data[key] || [];
    set(key, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const updateMed = (i, key, val) => {
    const next = [...(data.medicines || [])];
    next[i] = { ...next[i], [key]: val };
    set("medicines", next);
  };

  const addMed = () => set("medicines", [...(data.medicines || []), { name: "", strength: "", dosage: "", frequency: "", duration: "", instructions: "" }]);

  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="relative overflow-hidden rounded-3xl mb-6 grain" style={{ backgroundColor: "#22332A" }}>
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 12% 20%, rgba(207,133,107,0.25), transparent 45%), radial-gradient(circle at 88% 80%, rgba(76,120,95,0.30), transparent 50%)" }} />
        <div className="relative z-10 p-6 md:p-8 flex items-end justify-between flex-wrap gap-4 text-white">
          <div>
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-[0.18em] mb-2"><Stethoscope className="h-4 w-4" /> Consultation</div>
            <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">
              {savedId ? "Edit Consultation" : "New Consultation"}
            </h1>
            <p className="text-sm text-white/70 mt-1">
              {autosaveTag ? <span>Auto-saved at {autosaveTag}</span> : "Fill in the relevant sections — auto-saved after the first save."}
            </p>
          </div>
          <div className="flex gap-2">
            {savedId && (
              <a href={pdfUrl(savedId)} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="export-pdf-btn" className="bg-white/10 border-white/30 text-white hover:bg-white/20"><Download className="h-4 w-4 mr-2" />Export PDF</Button>
              </a>
            )}
            <Button data-testid="save-consultation-btn" onClick={onSave} disabled={busy} className="bg-white text-[#22332A] hover:bg-white/90">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Sticky nav */}
        <aside className="hidden lg:block lg:col-span-3">
          <Card className="p-2.5 sticky top-6 border-border/60">
            <div className="px-2 pt-1 pb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Sections</div>
            {SECTIONS.map((s, idx) => (
              <a key={s.id} href={`#${s.id}`}
                 onClick={() => setActive(s.id)}
                 className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-all
                  ${active === s.id ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-secondary"}`}>
                <span className={`grid place-items-center h-6 w-6 rounded-lg text-xs font-semibold shrink-0
                  ${active === s.id ? "bg-white/20 text-white" : "bg-secondary text-muted-foreground"}`}>{idx + 1}</span>
                {s.label}
              </a>
            ))}
          </Card>
        </aside>

        <div className="lg:col-span-9 space-y-6">
          {/* Patient */}
          <Card id="patient" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Patient</h2>
            <div className="space-y-2">
              <Label>Search Patient</Label>
              <PatientCombobox value={data.patient_id} patients={patients} onChange={(v) => set("patient_id", v)} />
              <div className="text-xs text-muted-foreground">Don&apos;t see your patient? <a href="/patients/new" className="underline">Add a new one</a>.</div>
            </div>
          </Card>

          {/* Consultation */}
          <Card id="consultation" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Consultation</h2>
            <div className="grid gap-4">
              <div className="space-y-1.5"><Label>Chief Complaint</Label>
                <Textarea data-testid="chief-complaint" value={data.chief_complaint} onChange={(e) => set("chief_complaint", e.target.value)} rows={2} /></div>
              <div className="space-y-1.5"><Label>History</Label>
                <Textarea data-testid="history" value={data.history} onChange={(e) => set("history", e.target.value)} rows={3} /></div>
            </div>
          </Card>

          {/* Exam */}
          <Card id="exam" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Clinical Examination</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                ["Temperature (°C)", "temperature"],
                ["Heart Rate (bpm)", "heart_rate"],
                ["Respiratory Rate (rpm)", "respiratory_rate"],
                ["Hydration", "hydration"],
                ["Lymph Node", "lymph_node"],
                ["Mucus Membrane", "mucus_membrane"],
              ].map(([label, key]) => (
                <div key={key} className="space-y-1.5"><Label>{label}</Label>
                  <Input data-testid={`exam-${key}`} value={data[key] || ""} onChange={(e) => set(key, e.target.value)} /></div>
              ))}
            </div>
          </Card>

          {/* Observations */}
          <Card id="observations" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Observations</h2>
            <Textarea data-testid="observations" value={data.observations} onChange={(e) => set("observations", e.target.value)} rows={5}
                      placeholder="Describe what you observe — e.g., ear infection, skin lesions…" />

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="mt-4 border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center text-center bg-muted/40 hover:bg-muted transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="observation-upload-zone"
            >
              <Upload className="h-7 w-7 text-muted-foreground mb-2" />
              <div className="text-sm font-medium">Click to upload or drag photos here</div>
              <div className="text-xs text-muted-foreground mt-1">JPG, PNG, HEIC · auto-compressed</div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                     onChange={(e) => onFiles(Array.from(e.target.files || []))} />
            </div>

            {(data.observation_images || []).length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {data.observation_images.map((img, i) => (
                  <div key={img.file_id} className="relative group">
                    <img src={fileUrl(img.file_id)} alt="obs" className="w-full aspect-square object-cover rounded-lg border border-border" />
                    <button type="button" className="absolute top-1 right-1 bg-background/80 backdrop-blur rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => set("observation_images", data.observation_images.filter((_, j) => j !== i))}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <Input placeholder="Caption" className="mt-1 text-xs h-8" value={img.caption || ""}
                           onChange={(e) => {
                             const next = [...data.observation_images]; next[i] = { ...next[i], caption: e.target.value };
                             set("observation_images", next);
                           }} />
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Diagnostic Tests */}
          <Card id="tests" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Diagnostic Tests</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {tests.map((t) => {
                const checked = data.diagnostic_tests.includes(t.name);
                return (
                  <label key={t.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors
                    ${checked ? "border-primary bg-primary/10" : "border-border hover:bg-muted/40"}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleList("diagnostic_tests", t.name)} />
                    <span className="text-sm">{t.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="space-y-1.5">
              <Label>Tests Notes (free text)</Label>
              <Textarea data-testid="tests-notes" value={data.tests_notes || ""} onChange={(e) => set("tests_notes", e.target.value)} rows={3}
                        placeholder="Notes about samples collected, lab references, additional tests…" />
            </div>
          </Card>

          {/* Findings */}
          <Card id="findings" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Findings</h2>
            <Textarea data-testid="findings-text" value={data.findings_text || ""} onChange={(e) => set("findings_text", e.target.value)} rows={6}
                      placeholder="Describe the findings — microscopic observations, lab results, gross findings, etc." />
          </Card>

          {/* Diagnosis */}
          <Card id="diagnosis" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Diagnosis</h2>
            <Textarea data-testid="diagnosis-text" value={data.diagnosis_text || ""} onChange={(e) => set("diagnosis_text", e.target.value)} rows={5}
                      placeholder="Primary, secondary, differential diagnosis — describe in your own words." />
            <div className="space-y-1.5 mt-3"><Label>ICD Code (optional)</Label>
              <Input data-testid="diag-icd_code" value={data.icd_code || ""} onChange={(e) => set("icd_code", e.target.value)} /></div>
          </Card>

          {/* Treatment */}
          <Card id="treatment" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Treatment</h2>
            <Textarea data-testid="treatment-text" value={data.treatment_text || ""} onChange={(e) => set("treatment_text", e.target.value)} rows={5}
                      placeholder="Treatment plan — describe procedures, therapies, instructions for staff…" />
          </Card>

          {/* Medicines */}
          <Card id="medicines" className="p-6 border-border/60 section-anchor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-primary text-xl font-semibold flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Medicines</h2>
              <Button type="button" variant="outline" size="sm" onClick={addMed} data-testid="add-medicine-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Medicine
              </Button>
            </div>
            <div className="space-y-3">
              {(data.medicines || []).length === 0 && <div className="text-sm text-muted-foreground">No medicines added.</div>}
              {(data.medicines || []).map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 md:col-span-3 space-y-1"><Label className="text-xs">Medicine</Label>
                    <Input value={m.name} onChange={(e) => updateMed(i, "name", e.target.value)} list="medlist" /></div>
                  <datalist id="medlist">{medicineCatalog.map((mc) => <option key={mc.id} value={mc.name} />)}</datalist>
                  <div className="col-span-6 md:col-span-2 space-y-1"><Label className="text-xs">Strength</Label>
                    <Input value={m.strength} onChange={(e) => updateMed(i, "strength", e.target.value)} /></div>
                  <div className="col-span-6 md:col-span-1 space-y-1"><Label className="text-xs">Dosage</Label>
                    <Input value={m.dosage} onChange={(e) => updateMed(i, "dosage", e.target.value)} /></div>
                  <div className="col-span-6 md:col-span-2 space-y-1"><Label className="text-xs">Frequency</Label>
                    <Input value={m.frequency} onChange={(e) => updateMed(i, "frequency", e.target.value)} /></div>
                  <div className="col-span-6 md:col-span-1 space-y-1"><Label className="text-xs">Duration</Label>
                    <Input value={m.duration} onChange={(e) => updateMed(i, "duration", e.target.value)} /></div>
                  <div className="col-span-11 md:col-span-2 space-y-1"><Label className="text-xs">Instructions</Label>
                    <Input value={m.instructions} onChange={(e) => updateMed(i, "instructions", e.target.value)} /></div>
                  <div className="col-span-1">
                    <Button type="button" size="icon" variant="ghost" onClick={() => set("medicines", data.medicines.filter((_, j) => j !== i))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1.5"><Label>Recommendations</Label>
              <Textarea value={data.recommendations} onChange={(e) => set("recommendations", e.target.value)} rows={3} /></div>
          </Card>

          {/* Follow-up */}
          <Card id="followup" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Follow-up & Reminders</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Follow-up Date</Label>
                <Input data-testid="followup-date" type="date" value={data.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Next Visit Notes</Label>
                <Textarea data-testid="followup-notes" value={data.follow_up_notes} onChange={(e) => set("follow_up_notes", e.target.value)} rows={2} /></div>
            </div>

            <div className="mt-5 pt-5 border-t border-border/60">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium">Schedule Reminders</div>
                  <div className="text-xs text-muted-foreground">Vaccinations, deworming, tick & flea — auto-added to patient care records on save.</div>
                </div>
                <Button type="button" variant="outline" size="sm" data-testid="add-reminder-btn"
                  onClick={() => set("follow_up_reminders", [...(data.follow_up_reminders || []), { kind: "Vaccination", name: "", next_due: "" }])}>
                  <Plus className="h-4 w-4 mr-1" />Add Reminder
                </Button>
              </div>
              <div className="space-y-2">
                {(data.follow_up_reminders || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4 md:col-span-3 space-y-1">
                      <Label className="text-xs">Kind</Label>
                      <Select value={r.kind} onValueChange={(v) => {
                        const next = [...data.follow_up_reminders]; next[i] = { ...next[i], kind: v }; set("follow_up_reminders", next);
                      }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Vaccination">Vaccination</SelectItem>
                          <SelectItem value="Deworming">Deworming</SelectItem>
                          <SelectItem value="Tick & Flea">Tick & Flea</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5 md:col-span-5 space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input value={r.name} placeholder="e.g. Rabies booster" onChange={(e) => {
                        const next = [...data.follow_up_reminders]; next[i] = { ...next[i], name: e.target.value }; set("follow_up_reminders", next);
                      }} />
                    </div>
                    <div className="col-span-3 md:col-span-3 space-y-1">
                      <Label className="text-xs">Next Due</Label>
                      <Input type="date" value={r.next_due} onChange={(e) => {
                        const next = [...data.follow_up_reminders]; next[i] = { ...next[i], next_due: e.target.value }; set("follow_up_reminders", next);
                      }} />
                    </div>
                    <div className="col-span-12 md:col-span-1">
                      <Button type="button" size="icon" variant="ghost" onClick={() => set("follow_up_reminders", data.follow_up_reminders.filter((_, j) => j !== i))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Vet */}
          <Card id="vet" className="p-6 border-border/60 section-anchor">
            <h2 className="font-primary text-xl font-semibold mb-4 flex items-center gap-2.5 before:content-[''] before:h-5 before:w-1.5 before:rounded-full before:bg-primary">Veterinarian</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Doctor Name</Label>
                <Input data-testid="doctor-name" value={data.doctor_name || ""} onChange={(e) => set("doctor_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Registration Number</Label>
                <Input data-testid="doctor-reg" value={data.registration_number || ""} onChange={(e) => set("registration_number", e.target.value)} /></div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Digital Signature</Label>
              <div className="flex items-center gap-3">
                {data.signature_file_id ? (
                  <div className="relative inline-block">
                    <img src={fileUrl(data.signature_file_id)} alt="signature" className="h-16 bg-white p-1 rounded border border-border" />
                    <button type="button" className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-1"
                            onClick={() => set("signature_file_id", null)}><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted text-sm">
                    <Upload className="h-4 w-4" /> Upload signature (PNG with transparent bg recommended)
                    <input type="file" accept="image/*" className="hidden" data-testid="signature-upload"
                           onChange={async (e) => {
                             const f = e.target.files?.[0]; if (!f) return;
                             const fd = new FormData(); fd.append("file", f);
                             try {
                               const { data: rec } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                               set("signature_file_id", rec.id);
                             } catch { toast.error("Upload failed"); }
                           }} />
                  </label>
                )}
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-3 pb-12">
            {savedId && (
              <a href={pdfUrl(savedId)} target="_blank" rel="noreferrer">
                <Button variant="outline"><Download className="h-4 w-4 mr-2" />Export PDF</Button>
              </a>
            )}
            <Button onClick={onSave} disabled={busy} className="font-semibold" size="lg">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Consultation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
